/**
 * feat-235 — the Tracks tab's RIGHT pane: the whole Lesson library you file FROM.
 *
 * It is a SOURCE, not a destination: it never changes when you reorder, and it
 * keeps its scroll position and search term while you move between tracks.
 *
 * "In no track" is a FILTER here, not a bucket in the track list (mockup §10).
 * A container implies its contents fell out of somewhere; a filter describes a
 * fact, and a lesson in no track is legitimate — a draft, a seasonal lesson
 * pulled for the winter. It is painted in the `preview` slate token, NEVER in
 * warning amber: amber is this codebase's warning family, and a number in amber
 * is a number someone will try to zero out. After migration 114 the count is 0
 * for every existing tenant, so the empty state is the copy nearly everyone
 * reads — it is an affirmative statement ("every Lesson is in at least one
 * Track") rather than a negation, which would read as a failed query.
 *
 * Filing 30+ lessons is the benchmark, so there are no per-row + buttons:
 * checkboxes plus ONE "Add N" commit make it one pass per track and let a
 * mis-click be corrected before anything is written.
 */

import React from 'react';
import type { LearnTrack, QuizUnit } from '../../../../services/quizContentAdminService';
import { QUIZ_TERMS, termCount } from '../../quizTerminology';
import { ATTENTION_INK } from './attentionClasses';
import { formatReleaseInstant, isScheduledLater } from '../lessonSchedule';

export type LibraryFilter = 'all' | 'not-in-track' | 'no-track';

/**
 * feat-238 — the non-colour half of the Lesson status pill.
 *
 * Deliberately the same literal as `TrackDetailPane`'s, not a shared import and
 * not a variant: the design notes specify identical strings in both panes, and
 * a Lesson's status must not look like one thing here and another there. AC13
 * asserts the two panes agree, so drift is caught rather than merely hoped
 * against; extracting a shared pill component was ruled out of scope because it
 * would pull in the Lessons tab (`MasterDetailTree.tsx`), which this feature
 * matches rather than edits.
 *
 * NOTE the pill must stay a DIRECT child of the row's flex container —
 * `min-width` and `text-align` do not apply to a non-replaced inline box, so
 * wrapping it silently un-sizes it and the column goes ragged with no error.
 */
const LESSON_STATUS_PILL =
  'text-[10px] font-bold rounded-full px-1.5 py-0.5 shrink-0 min-w-[48px] text-center';

export interface LessonLibraryPaneProps {
  units: QuizUnit[];
  /** The selected track — the destination an "Add N" commits to. */
  track: LearnTrack | null;
  /** Server-computed set difference over the whole tenant; never re-derived here. */
  unassignedLessonIds: string[];
  /** How many tracks each lesson is in — the dots after a title. */
  trackCountByLesson: Record<string, number>;
  /** feat-242 — H1's population, derived once in `trackGraph.buildCurriculumMap`
   *  and passed in rather than recomputed here, so the footer and the Map can
   *  never disagree about which Lessons nobody can open.
   *
   *  OPTIONAL, and it defaults to empty on purpose: a caller that does not pass
   *  it renders the footer exactly as it ships today. That keeps AC14's
   *  byte-identical line true by construction for every existing call site.
   *
   *  NOTE this set is BROADER than the footer's own population — it also holds
   *  Lessons filed only into Hidden tracks, which are not unfiled. The footer
   *  intersects it with the unfiled population and never prints its size. */
  unreachableLessonIds?: ReadonlySet<string>;
  checked: ReadonlySet<string>;
  onToggle: (lessonId: string) => void;
  onClear: () => void;
  onCommitAdd: () => void;
  search: string;
  onSearch: (q: string) => void;
  filter: LibraryFilter;
  onFilter: (f: LibraryFilter) => void;
  busy?: boolean;
}

const Chip: React.FC<{
  label: string;
  count: number;
  pressed: boolean;
  disabled: boolean;
  slate?: boolean;
  onClick: () => void;
}> = ({ label, count, pressed, disabled, slate, onClick }) => (
  <button
    type="button"
    aria-pressed={pressed}
    disabled={disabled}
    onClick={onClick}
    className={`text-[11.5px] font-bold rounded-full px-2.5 py-1 border ${
      pressed
        ? slate
          ? 'bg-preview border-preview text-white'
          : 'bg-ink border-ink text-white'
        : 'bg-white border-softBorder text-inkMuted hover:bg-cream'
    } ${disabled ? 'opacity-45 cursor-default' : ''}`}
  >
    {label} {count}
  </button>
);

export const LessonLibraryPane: React.FC<LessonLibraryPaneProps> = ({
  units,
  track,
  unassignedLessonIds,
  trackCountByLesson,
  unreachableLessonIds,
  checked,
  onToggle,
  onClear,
  onCommitAdd,
  search,
  onSearch,
  filter,
  onFilter,
  busy = false,
}) => {
  const inTrack = new Set(track?.lesson_ids ?? []);
  const unassigned = new Set(unassignedLessonIds);

  const notInTrackCount = track ? units.filter((u) => !inTrack.has(u.id)).length : 0;
  const noTrackCount = units.filter((u) => unassigned.has(u.id)).length;

  // feat-242 — the footer's UNFILED population, split into the half that is a
  // problem and the half that is not. Intersected with the unfiled set, never
  // taken whole: H1 also holds Lessons filed only into Hidden tracks, and those
  // belong to the Map, not to this line (spec §5's population note).
  //
  // Everything left over after the split is a draft by construction, not by
  // assumption: the split only renders when the unreachable count is > 0, which
  // requires >= 2 active tracks, and under that condition an unfiled Lesson that
  // is On is necessarily unreachable. So the remainder is exactly the Hidden ones.
  const noTrackUnreachableCount = unreachableLessonIds
    ? units.filter((u) => unassigned.has(u.id) && unreachableLessonIds.has(u.id)).length
    : 0;
  const noTrackDraftCount = noTrackCount - noTrackUnreachableCount;

  const q = search.trim().toLowerCase();
  const visible = units.filter((u) => {
    if (q && !(u.title || '').toLowerCase().includes(q)) return false;
    if (filter === 'not-in-track') return !inTrack.has(u.id);
    if (filter === 'no-track') return unassigned.has(u.id);
    return true;
  });

  const selectedCount = checked.size;

  return (
    <aside className="border-l border-softBorder bg-cream/40 flex flex-col min-h-0 overflow-hidden">
      <div className="p-3 border-b border-softBorder shrink-0">
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={`Find a ${QUIZ_TERMS.lesson.singular}…`}
            aria-label={`Find a ${QUIZ_TERMS.lesson.singular}`}
            className="w-full text-sm bg-white border border-softBorder rounded-full pl-9 pr-3 py-2 placeholder:text-inkMuted/70 focus:outline-none focus:ring-2 focus:ring-coral/40"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-inkMuted/60 text-sm">⌕</span>
        </div>
        <div className="flex gap-1.5 flex-wrap mt-2">
          <Chip
            label="All"
            count={units.length}
            pressed={filter === 'all'}
            disabled={false}
            onClick={() => onFilter('all')}
          />
          <Chip
            label={`Not in ${track?.title || QUIZ_TERMS.track.singular}`}
            count={notInTrackCount}
            pressed={filter === 'not-in-track'}
            // Disabled rather than hidden while it can only be 0 (mockup §6): a
            // control that appears and vanishes makes the pane a different shape
            // every visit, and hides the concept before it matters.
            disabled={!track || notInTrackCount === 0}
            onClick={() => onFilter('not-in-track')}
          />
          <Chip
            label={`In no ${QUIZ_TERMS.track.singular}`}
            count={noTrackCount}
            pressed={filter === 'no-track'}
            disabled={noTrackCount === 0}
            slate
            onClick={() => onFilter('no-track')}
          />
        </div>
      </div>

      {filter === 'no-track' && noTrackCount > 0 && (
        <div className="mx-3 mt-3 rounded-xl border border-preview-border bg-preview-light text-ink/80 text-xs px-3 py-2">
          These {noTrackCount} aren't in any {QUIZ_TERMS.track.singular} yet, so nobody sees them.
          That's fine if they're drafts.
        </div>
      )}

      {visible.length === 0 ? (
        <div className="flex-1 grid place-items-center p-7 text-center">
          <div>
            <div className="text-sm font-bold">
              {filter === 'no-track'
                ? `Every ${QUIZ_TERMS.lesson.singular} is in at least one ${QUIZ_TERMS.track.singular}.`
                : filter === 'not-in-track' && track
                  ? `All ${units.length} ${QUIZ_TERMS.lesson.plural} are in ${track.title || '(untitled)'}.`
                  : q
                    ? `No matches for “${search}”.`
                    : `No ${QUIZ_TERMS.lesson.plural} in this tenant yet.`}
            </div>
            <div className="text-xs text-inkMuted mt-1 max-w-[30ch] mx-auto">
              {filter === 'no-track'
                ? `Nothing to do here. ${QUIZ_TERMS.lesson.plural} show up in this filter when you take them out of their last ${QUIZ_TERMS.track.singular}.`
                : filter === 'not-in-track'
                  ? `Make another ${QUIZ_TERMS.track.singular} and you can start splitting them by role.`
                  : ''}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto p-2 flex flex-col gap-0.5">
          {visible.map((unit) => {
            const already = inTrack.has(unit.id);
            const isChecked = checked.has(unit.id);
            const dots = trackCountByLesson[unit.id] ?? 0;
            return (
              <label
                key={unit.id}
                data-testid={`library-row-${unit.id}`}
                className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] ${
                  isChecked ? 'bg-coral-light' : 'hover:bg-white/80'
                } ${already ? 'text-inkMuted' : 'text-ink cursor-pointer'}`}
                title={
                  already
                    ? `Already in ${track?.title ?? ''} — remove it from the ordered list instead`
                    : dots === 0
                      ? `In no ${QUIZ_TERMS.track.singular}`
                      : `${termCount(dots, 'track')}`
                }
              >
                <input
                  type="checkbox"
                  className="w-3.5 h-3.5 accent-coral"
                  // Lessons already in this track render CHECKED AND DIMMED
                  // rather than hidden, so "is this one in?" is answered without
                  // changing filters. The checkbox is not a control there —
                  // unchecking happens in the centre pane, where the position
                  // being removed is visible.
                  checked={already || isChecked}
                  disabled={already || busy}
                  onChange={() => onToggle(unit.id)}
                  aria-label={unit.title || '(untitled)'}
                />
                <span className="flex-1 truncate font-semibold">{unit.title || '(untitled)'}</span>
                {/* feat-238 — hidden-ness, carried by the PILL ALONE in this pane.
                    No `opacity-60` here, ever: this pane already spends a dim
                    (`text-inkMuted` on the row, above) on "already in the selected
                    Track". A second dim meaning something else would make the two
                    states indistinguishable — which is the confusion the feature
                    exists to remove (AC5). The pill sets its own `text-*`, so the
                    row-level dim never reaches it and `On` stays green on an
                    already-in-Track row. Read-only: no handler, not a button. */}
                {/* feat-256 — the third state, `Later`: Active but held back until
                    an instant that has not arrived. `bg-preview-light text-preview`
                    is the ONLY change from the existing two variants; the shape
                    classes are untouched, so `min-w-[48px]` keeps governing and all
                    three states still measure 48px (feat-238 AC13). `Later` was
                    chosen by measurement precisely so it stays under the `Hidden`
                    ceiling — see the note at the MasterDetailTree site.

                    NO `opacity-*` here either, for this pane's own reason recorded
                    above: it already spends its one dim on "already in the selected
                    Track", and a second dim would make the two indistinguishable.

                    Still read-only, still not a button — the third state changes
                    the vocabulary, not the affordance. `title` is 7.15. */}
                <span
                  data-testid={`library-row-status-${unit.id}`}
                  title={isScheduledLater(unit) ? `Available ${formatReleaseInstant(unit.available_at)}` : undefined}
                  className={`${
                    !unit.active
                      ? 'bg-gray-100 text-inkMuted'
                      : isScheduledLater(unit)
                        ? 'bg-preview-light text-preview'
                        : 'bg-success-light text-success-on'
                  } ${LESSON_STATUS_PILL}`}
                >
                  {!unit.active ? 'Hidden' : isScheduledLater(unit) ? 'Later' : 'On'}
                </span>
                {/* Dots up to five, then a NUMERAL — Grove launches with seven
                    tracks, and five dots for six-or-seven is a count that reads
                    as exact and isn't. (The row `title` carries the true figure
                    either way, but the thing being scanned is the glyph.) */}
                <span
                  className="flex gap-[3px] shrink-0 items-center"
                  data-testid={`library-row-tracks-${unit.id}`}
                  aria-hidden
                >
                  {dots > 5 ? (
                    <span className="text-[10px] font-bold text-inkMuted/70 tabular-nums">{dots}</span>
                  ) : (
                    Array.from({ length: dots }).map((_, i) => (
                      <i key={i} className="w-1.5 h-1.5 rounded-full bg-inkMuted/35 inline-block" />
                    ))
                  )}
                </span>
              </label>
            );
          })}
        </div>
      )}

      {selectedCount > 0 && (
        <div className="border-t border-softBorder bg-coral-light px-3 py-2.5 flex items-center justify-between gap-2 shrink-0">
          <span className="text-xs font-bold text-coral">{selectedCount} selected</span>
          <span className="flex gap-2">
            <button
              type="button"
              onClick={onClear}
              disabled={busy}
              className="text-[11px] font-bold rounded-full px-2.5 py-1 border border-softBorder bg-white text-ink disabled:opacity-50"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={onCommitAdd}
              disabled={busy || !track}
              className="text-[11px] font-bold rounded-full px-2.5 py-1 bg-coral text-white disabled:opacity-50"
            >
              Add {selectedCount} to {track?.title || QUIZ_TERMS.track.singular}
            </button>
          </span>
        </div>
      )}

      {/* Discoverability without escalation: a quiet always-on count in a pane
          the author is already reading — no badge, no colour alarm.

          feat-242 — ONE LINE, TWO COLOUR FAMILIES, ON PURPOSE. The header above
          says the unfiled count is painted in `preview` slate and NEVER in
          amber, "because a number in amber is a number someone will try to zero
          out". That is correct about THIS number and it is untouched: the whole
          population stays slate, because an unfiled Lesson is legitimate.

          For the new sub-count it inverts. "N Lessons that are On and that
          nobody can open" is a number where someone trying to zero it out is the
          INTENDED behaviour — driving it to zero means every Lesson the author
          switched On is in a Track the crew can reach, which is the state the
          author already believes they are in. Same mechanism, opposite and
          correct effect, because the populations differ. TOKENS.md §1o.

          If you find yourself "correcting" one half to match the other, you are
          erasing the distinction. Neither the comment nor this is wrong; they
          are about different numbers. */}
      <div
        data-testid="library-footer-count"
        className="border-t border-softBorder px-3 py-2 text-[11.5px] text-inkMuted tabular-nums shrink-0"
      >
        {termCount(units.length, 'lesson')} ·{' '}
        <b className="text-preview font-extrabold">
          {noTrackCount} in no {QUIZ_TERMS.track.singular}
        </b>
        {/* Renders ONLY when the unreachable count is > 0. On every tenant where
            it is 0 — which is every tenant today, and Ladle & Leaf permanently —
            this branch emits nothing and the line above is byte-identical to
            what ships now (AC14). */}
        {noTrackUnreachableCount > 0 && (
          <>
            {' — '}
            <b data-testid="library-footer-unreachable" className={`${ATTENTION_INK} font-extrabold`}>
              {noTrackUnreachableCount} nobody can open
            </b>
            {noTrackDraftCount > 0 && (
              <>
                {', '}
                <b className="text-preview font-extrabold">
                  {noTrackDraftCount} {noTrackDraftCount === 1 ? 'draft' : 'drafts'}
                </b>
              </>
            )}
          </>
        )}
      </div>
    </aside>
  );
};

export default LessonLibraryPane;
